import type { CapabilityBinding, ModuleMeta } from '../../core/types';
import type { VmFieldKey } from './fields';
import { VM_COMPUTE_RESOURCE_CODES } from './resourceConfigs';

export const VM_CAPABILITIES = [
  {
    key: 'base-info',
    pathPrefix: '',
    options: { nameMaxLength: 63, descriptionMaxLength: 256 },
  },
  {
    key: 'network',
    pathPrefix: 'resource.network',
    options: { showSecurityGroup: true },
  },
  {
    key: 'run-policy',
    pathPrefix: '',
    options: { loadFromApi: true },
  },
  {
    key: 'image-vm',
    pathPrefix: '',
    options: {
      imageListKind: 'cloudHost',
      diskSegment: 'systemDisk',
    },
  },
  {
    key: 'compute',
    pathPrefix: 'resource',
    options: {
      showGpu: true,
      showBandwidth: true,
      vendorDeviceWorkloadType: 'VirtualMachine',
      resourceCodes: VM_COMPUTE_RESOURCE_CODES,
    },
  },
  {
    key: 'storage',
    pathPrefix: '',
    options: { listSegment: 'dataDisks' },
  },
  {
    key: 'dns',
    pathPrefix: 'resource.network',
  },
  {
    key: 'service',
    pathPrefix: 'resource',
    options: { listSegment: 'service', requireAtLeastOne: false },
  },
] as const satisfies readonly CapabilityBinding[];

export const VM_MODULES = [
  {
    key: 'baseInfo',
    title: '基本信息',
    capabilities: [{ key: 'base-info', pathPrefix: '' }],
    fields: ['base.name', 'base.description', 'base.workspace', 'base.namespace', 'base.datacenter'],
    /** 与 presetConfig / VM_SKU_MODULE_META 一致（表单序列化路径） */
    presetCheckFields: ['name'],
  },
  {
    key: 'network',
    title: '网络设置',
    capabilities: [{ key: 'network', pathPrefix: 'resource.network', options: { showSecurityGroup: true } }],
    fields: ['network.vpc', 'network.subnet', 'network.securitygroup'],
    presetCheckFields: ['resource.network.vpc.name'],
  },
  {
    key: 'systemSetting',
    title: '系统配置',
    capabilities: [
      { key: 'run-policy', pathPrefix: '' },
      {
        key: 'image-vm',
        pathPrefix: '',
        options: {
          imageListKind: 'cloudHost',
          diskSegment: 'systemDisk',
        },
      },
    ],
    fields: ['runPolicy.strategy', 'disk'],
    presetCheckFields: ['runStrategy', 'systemDisk'],
    resetFieldPaths: ['runStrategy', 'systemDisk'],
  },
  {
    key: 'resource',
    title: '资源配置',
    capabilities: [
      {
        key: 'compute',
        pathPrefix: 'resource',
        options: {
          resourceCodes: VM_COMPUTE_RESOURCE_CODES,
          vendorDeviceWorkloadType: 'VirtualMachine',
        },
      },
    ],
    fields: ['system.architecture', 'compute.cpu', 'compute.memory', 'compute.gpu', 'compute.bandwidth'],
    presetCheckFields: ['resource.cpu', 'resource.memory', 'resource.gpu', 'resource.network.Bandwidth'],
    resetFieldPaths: [
      'system',
      'resource.cpu',
      'resource.memory',
      'resource.gpu',
      'resource.cpuPricing',
      'resource.memoryPricing',
      'resource.gpuPricing',
      'resource.network.Bandwidth',
      'resource.network.bandwidthPricing',
    ],
  },
  {
    key: 'service',
    title: '服务配置',
    capabilities: [
      {
        key: 'service',
        pathPrefix: 'resource',
        options: { listSegment: 'service', requireAtLeastOne: false },
      },
    ],
    fields: ['service.list'],
    presetCheckFields: ['resource.network.service'],
    resetFieldPaths: ['resource.network.service'],
  },
  {
    key: 'dataDisk',
    title: '存储配置',
    capabilities: [{ key: 'storage', pathPrefix: '', options: { listSegment: 'dataDisks' } }],
    fields: ['storage.list'],
    presetCheckFields: ['dataDisks'],
    resetFieldPaths: ['dataDisks'],
  },
  {
    key: 'dns',
    title: 'DNS配置',
    capabilities: [{ key: 'dns', pathPrefix: 'resource.network' }],
    fields: ['network.dns.nameservers', 'network.dns.searches', 'network.dns.options'],
    presetCheckFields: [
      'resource.network.dnsConfig.nameservers',
      'resource.network.dnsConfig.searches',
      'resource.network.dnsConfig.options',
    ],
    resetFieldPaths: ['resource.network.dnsConfig'],
  },
] as const satisfies readonly ModuleMeta<VmFieldKey>[];

export const VM_MODULE_MAP = Object.fromEntries(VM_MODULES.map((item) => [item.key, item]));
